// React Query hooks for the Player Stats query drawer (FE4): the metric
// catalogue, the position lookup, and the configurable league-wide stats query.
// Each is disabled until its inputs are known.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { PositionsResponse } from "@catbento/shared";
import { apiGet, apiSend } from "../api.js";
import { buildStatsBody, type StatsConfig } from "../widgets/statsQuery.js";

const ONE_HOUR = 60 * 60 * 1000;

// The metric catalogue shape (bare array). No shared contract exists — the
// backend returns soccer-aggregates' `PlayerMetricCategoryEnriched[]` verbatim,
// so we mirror the fields we render here. Keep in sync if the engine's shape
// changes (it's the same source the backend uses).
export interface MetricInfo {
  key: string;
  name: string;
  shortName: string;
  // outputType drives value formatting (integer / decimal / percentage); definition
  // is the tooltip text. Both come from the engine's enriched catalogue — present in
  // the payload, added here when the Full Stats widget (FE #5) began using them.
  outputType?: string | null;
  definition?: string;
}
export interface MetricSubcategory {
  id: string;
  name: string;
  playerMetrics: MetricInfo[];
}
export interface MetricCategory {
  id: string;
  name: string;
  subcategories: MetricSubcategory[];
}

/** The player-metric catalogue for the drawer's metric picker. `per90` swaps in
 *  the per-90 variants — a different key set, so it's part of the query key.
 *  Cached long: static reference data that only changes with the engine. */
export function useMetricCategories(per90: boolean): UseQueryResult<MetricCategory[], Error> {
  return useQuery({
    queryKey: ["metric-categories", per90],
    queryFn: () => apiGet<MetricCategory[]>(`/player-metric-categories?per90=${per90}`),
    staleTime: ONE_HOUR,
  });
}

/** The position lookup for the position filter. Static ref data — cache long. */
export function usePositions(): UseQueryResult<PositionsResponse, Error> {
  return useQuery({
    queryKey: ["positions"],
    queryFn: () => apiGet<PositionsResponse>("/positions"),
    staleTime: ONE_HOUR,
  });
}

/** The stats-table rows — arbitrary metric/dimension columns per row. */
export interface StatsQueryResponse {
  rows: Record<string, number | string | null>[];
}

/**
 * The configurable league-wide stats query. Disabled until competition+season
 * are known AND at least one metric is selected (the backend 400s on empty
 * `keys`). `retry: 1` because a cold VPC Lambda can transient-500 on the first
 * hit; this is also the heavy path (~2.6s warm, up to ~16s cold — the widget
 * shows a clear loading state). React Query caches repeat views by the full
 * config, so re-opening an unchanged table is instant.
 */
export function useStatsQuery(
  competitionId: number | null,
  seasonId: number | null,
  config: StatsConfig
): UseQueryResult<StatsQueryResponse, Error> {
  const ready = competitionId !== null && seasonId !== null && config.keys.length > 0;
  return useQuery({
    queryKey: ["player-stats-table", competitionId, seasonId, config],
    enabled: ready,
    retry: 1,
    queryFn: () =>
      apiSend<StatsQueryResponse>(
        "POST",
        "/player-stats",
        // competitionId/seasonId are non-null here (query is disabled otherwise).
        buildStatsBody(competitionId as number, seasonId as number, config)
      ),
  });
}
